import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Calendar, ChevronDown, ChevronRight, ClipboardList, History } from 'lucide-react'
import { Card, CardBody, CardHeader } from '@/components/ui/Card'
import ProgressBar from './ProgressBar'
import { formatPlanDate } from '@/utils/date'
import { matchingService } from '@/services/matchingService'
import { planService } from '@/services/planService'
import { Plan } from '@/types/plan'

const ActivePlanCard: React.FC = () => {
  const [plan, setPlan] = useState<Plan | null>(null)
  const [relationshipId, setRelationshipId] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [expandedGoalIds, setExpandedGoalIds] = useState<string[]>([])

  useEffect(() => {
    let cancelled = false

    const load = async () => {
      try {
        const relationships = await matchingService.getMyRelationships()
        const active = (Array.isArray(relationships) ? relationships : []).find(
          (r) => r.status === 'active'
        )
        if (!active) return
        if (!cancelled) setRelationshipId(active.id)

        const plans = await planService.getPlansForRelationship(active.id)
        const activePlan = (Array.isArray(plans) ? plans : []).find((p) => p.status === 'active')
        if (!cancelled && activePlan) {
          setPlan(activePlan)
        }
      } catch {
        // Plan card is non-critical on the dashboard
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    load()
    return () => {
      cancelled = true
    }
  }, [])

  const toggleGoal = (goalId: string) => {
    setExpandedGoalIds((prev) =>
      prev.includes(goalId) ? prev.filter((id) => id !== goalId) : [...prev, goalId]
    )
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ClipboardList className="w-5 h-5 text-primary-600" />
            <h2 className="text-lg font-semibold text-gray-900">Your Plan</h2>
          </div>
          {relationshipId && (
            <Link
              to={`/plans/relationship/${relationshipId}`}
              className="inline-flex items-center gap-1 text-sm text-primary-600 hover:underline"
            >
              <History className="w-4 h-4" />
              My Plans
            </Link>
          )}
        </div>
      </CardHeader>
      <CardBody>
        {loading && (
          <div className="space-y-3 animate-pulse">
            <div className="h-6 bg-gray-100 rounded-md w-2/3" />
            <div className="h-3 bg-gray-100 rounded-md" />
          </div>
        )}

        {!loading && !plan && (
          <p className="text-gray-600 text-sm py-4 text-center">
            Your mentor hasn't created an active plan yet.
          </p>
        )}

        {!loading && plan && (
          <div>
            <div className="flex items-center justify-between mb-1">
              <h3 className="text-base font-semibold text-gray-900">{plan.title}</h3>
              <div className="flex items-center gap-1 text-xs text-gray-500">
                <Calendar className="w-3 h-3" />
                <span>Ends {formatPlanDate(plan.endDate)}</span>
              </div>
            </div>

            <ProgressBar value={plan.progressPercentage} label="Overall plan progress" />

            {plan.goals.length > 0 && (
              <ul className="mt-4 space-y-2">
                {plan.goals.map((goal) => {
                  const expanded = expandedGoalIds.includes(goal.id)
                  return (
                    <li key={goal.id} className="border rounded-md">
                      <button
                        type="button"
                        onClick={() => toggleGoal(goal.id)}
                        className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm text-gray-900 hover:bg-gray-50"
                      >
                        {expanded ? (
                          <ChevronDown className="w-4 h-4 text-gray-400" />
                        ) : (
                          <ChevronRight className="w-4 h-4 text-gray-400" />
                        )}
                        <span className="flex-1">{goal.title}</span>
                        <span className="text-xs text-gray-500">{goal.progressPercentage}%</span>
                      </button>
                      {expanded && (
                        <div className="px-3 pb-3">
                          <ProgressBar value={goal.progressPercentage} label={`${goal.title} progress`} />
                        </div>
                      )}
                    </li>
                  )
                })}
              </ul>
            )}

            <div className="mt-4">
              <Link
                to={`/plans/${plan.id}`}
                className="text-sm text-primary-600 hover:underline font-medium"
              >
                View plan →
              </Link>
            </div>
          </div>
        )}
      </CardBody>
    </Card>
  )
}

export default ActivePlanCard
